/*
  Reference link: https://shopify.dev/docs/themes/navigation-search/search/predictive-search

  Hardened + fixed:
  - FIX: maxHeight always with "px"
  - No optional chaining (?.) for better WebView compatibility
  - Fallback for startsWith
  - Fallback SearchForm if missing (prevents "SearchForm is not defined")
  - Safe fetch + safe DOM
*/

(function () {
  // ---- helpers (safe & backwards-compatible) ----
  function safeReplaceAll(str, find, repl) {
    return String(str).split(find).join(repl);
  }

  function safeLower(str) {
    return String(str || "").toLowerCase();
  }

  function safeTrim(str) {
    return String(str || "").trim();
  }

  function safeStartsWith(haystack, needle) {
    haystack = String(haystack || "");
    needle = String(needle || "");
    if (!needle) return true;
    if (typeof haystack.startsWith === "function") return haystack.startsWith(needle);
    return haystack.indexOf(needle) === 0;
  }

  function safeDebounce(fn, wait) {
    // Use global debounce if exists
    if (typeof window.debounce === "function") return window.debounce(fn, wait);
    if (typeof debounce === "function") return debounce(fn, wait);

    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        try {
          fn.apply(ctx, args);
        } catch (e) {}
      }, wait);
    };
  }

  function safeAbortController() {
    try {
      if (typeof AbortController !== "undefined") return new AbortController();
    } catch (e) {}
    return null;
  }

  function isAbortError(err) {
    try {
      if (!err) return false;
      if (err.name === "AbortError") return true;
      if (err.code === 20) return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  function safeQuery(root, sel) {
    try {
      return root ? root.querySelector(sel) : null;
    } catch (e) {
      return null;
    }
  }

  function safeQueryAll(root, sel) {
    try {
      return root ? root.querySelectorAll(sel) : [];
    } catch (e) {
      return [];
    }
  }

  // ---- fallback SearchForm (if theme doesn't define it) ----
  if (typeof SearchForm === "undefined") {
    // Minimal class just to avoid hard crash; does not change your markup
    window.SearchForm = class SearchForm extends HTMLElement {
      constructor() {
        super();
        this.input = safeQuery(this, 'input[type="search"], input[name="q"], input');
        this.resetButton = safeQuery(this, 'button[type="reset"], [data-reset]');
        this.input && this.input.addEventListener("input", this.onChange.bind(this));
        if (this.input && this.input.form) {
          this.input.form.addEventListener("reset", this.onFormReset.bind(this));
        }
      }
      onChange() {}
      onFormReset() {}
      shouldResetForm() { return true; }
    };
  }

  // ---- guard: customElements required ----
  if (typeof customElements === "undefined") return;

  class PredictiveSearch extends SearchForm {
    constructor() {
      super();

      this.cachedResults = {};
      this.predictiveSearchResults = safeQuery(this, "[data-predictive-search]");
      this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
      this.isOpen = false;

      this.abortController = safeAbortController();
      this.searchTerm = "";

      // Guard: if markup is incomplete, don't bind listeners
      if (!this.input || !this.input.form || !this.predictiveSearchResults) {
        return;
      }

      this.setupEventListeners();
    }

    setupEventListeners() {
      try {
        this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
        this.input.form.addEventListener("reset", this.onFormReset.bind(this));

        this.select = safeQuery(this, "select");
        if (this.select) {
          this.select.addEventListener(
            "input",
            safeDebounce(function () { this.onChange(); }, 300).bind(this)
          );
        }

        this.input.addEventListener("focus", this.onFocus.bind(this));
        this.addEventListener("focusout", this.onFocusOut.bind(this));
        this.addEventListener("keyup", this.onKeyup.bind(this));
        this.addEventListener("keydown", this.onKeydown.bind(this));
      } catch (e) {}
    }

    getQuery() {
      return safeTrim(this.input && this.input.value);
    }

    getSearchFilterType() {
      if (!this.select) return "";
      return this.select.dataset ? (this.select.dataset.type || "") : "";
    }

    getSearchFilterTypeQuery() {
      if (!this.select) return "";
      return safeTrim(this.select.value);
    }

    onChange() {
      try { if (super.onChange) super.onChange(); } catch (e) {}

      var newSearchTerm = this.getQuery();

      try {
        if (!this.searchTerm || !safeStartsWith(newSearchTerm, this.searchTerm)) {
          var wrapper = safeQuery(this, "#predictive-search-results-groups-wrapper");
          if (wrapper) wrapper.remove();
        }
      } catch (e) {}

      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      try {
        if (!this.getQuery().length || safeQuery(this, '[aria-selected="true"] a')) {
          event.preventDefault();
        }
      } catch (e) {}
    }

    onFormReset(event) {
      try {
        if (super.onFormReset) super.onFormReset(event);
        if (!super.shouldResetForm || super.shouldResetForm()) {
          this.searchTerm = "";
          if (this.abortController && typeof this.abortController.abort === "function") {
            try { this.abortController.abort(); } catch (e) {}
          }
          this.abortController = safeAbortController();
          this.closeResults(true);
        }
      } catch (e) {}
    }

    onFocus() {
      var currentSearchTerm = this.getQuery();
      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute("results") === "true") {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(function () {
        try {
          if (!this.contains(document.activeElement)) this.close();
        } catch (e) {}
      }.bind(this), 0);
    }

    onKeyup(event) {
      try {
        if (!this.getQuery().length) this.close(true);
        event.preventDefault();

        switch (event.code) {
          case "ArrowUp":
            this.switchOption("up");
            break;
          case "ArrowDown":
            this.switchOption("down");
            break;
          case "Enter":
            this.selectOption();
            break;
        }
      } catch (e) {}
    }

    onKeydown(event) {
      try {
        if (event.code === "ArrowUp" || event.code === "ArrowDown") {
          event.preventDefault();
        }
      } catch (e) {}
    }

    updateSearchForTerm(previousTerm, newTerm) {
      try {
        var el = safeQuery(this, "[data-predictive-search-search-for-text]");
        var currentText = el ? el.innerText : "";
        if (!currentText) return;

        previousTerm = String(previousTerm || "");
        newTerm = String(newTerm || "");

        // Avoid weird replacements when previousTerm is empty
        if (!previousTerm) return;

        // no optional chaining
        var matches = null;
        try {
          matches = currentText.match(new RegExp(previousTerm, "g"));
        } catch (e) {
          matches = null;
        }
        if (matches && matches.length > 1) return;

        el.innerText = currentText.replace(previousTerm, newTerm);
      } catch (e) {}
    }

    switchOption(direction) {
      try {
        if (!this.getAttribute("open")) return;

        var moveUp = direction === "up";
        var selectedElement = safeQuery(this, '[aria-selected="true"]');

        var nodeList = safeQueryAll(this, "li, button.predictive-search__item");
        var allVisible = [];
        for (var i = 0; i < nodeList.length; i++) {
          var el = nodeList[i];
          try {
            if (el.offsetParent !== null) allVisible.push(el);
          } catch (e) {
            allVisible.push(el);
          }
        }

        if (moveUp && !selectedElement) return;

        var selectedIndex = -1;
        for (var j = 0; j < allVisible.length; j++) {
          if (allVisible[j] === selectedElement) { selectedIndex = j; break; }
        }

        this.statusElement = this.statusElement || safeQuery(this, ".predictive-search-status");
        if (this.statusElement) this.statusElement.textContent = "";

        var activeIndex = 0;
        if (!moveUp && selectedElement) {
          activeIndex = selectedIndex === allVisible.length - 1 ? 0 : selectedIndex + 1;
        } else if (moveUp) {
          activeIndex = selectedIndex === 0 ? allVisible.length - 1 : selectedIndex - 1;
        }

        if (activeIndex === selectedIndex) return;

        var activeEl = allVisible[activeIndex];
        if (!activeEl) return;

        activeEl.setAttribute("aria-selected", true);
        if (selectedElement) selectedElement.setAttribute("aria-selected", false);

        if (activeEl.id) this.input.setAttribute("aria-activedescendant", activeEl.id);
      } catch (e) {}
    }

    selectOption() {
      try {
        var selected = safeQuery(this, '[aria-selected="true"] a, button[aria-selected="true"]');
        if (selected) selected.click();
      } catch (e) {}
    }

    getSearchResults(searchTerm) {
      try {
        var filterType = this.getSearchFilterType();
        var filterTypeQuery = this.getSearchFilterTypeQuery();

        var queryKey =
          safeLower(safeReplaceAll(searchTerm, " ", "-")) +
          "-" +
          safeLower(filterType) +
          "-" +
          safeLower(safeReplaceAll(filterTypeQuery, " ", "-"));

        this.setLiveRegionLoadingState();

        if (this.cachedResults[queryKey]) {
          this.renderSearchResults(this.cachedResults[queryKey]);
          return;
        }

        var limit = (this.dataset && this.dataset.limit) ? this.dataset.limit : "";
        var resultTypes = (this.dataset && this.dataset.types) ? this.dataset.types : "";
        var showUnavailable = (this.dataset && this.dataset.unavailable) ? this.dataset.unavailable : "";

        var query = filterTypeQuery
          ? encodeURIComponent(filterType + ":" + filterTypeQuery + "+* " + searchTerm)
          : encodeURIComponent(searchTerm);

        // Abort previous request (if supported)
        if (this.abortController && typeof this.abortController.abort === "function") {
          try { this.abortController.abort(); } catch (e) {}
          this.abortController = safeAbortController();
        }

        var signal = this.abortController ? this.abortController.signal : undefined;

        if (typeof routes === "undefined" || !routes.predictive_search_url) {
          this.close();
          return;
        }

        var url =
          routes.predictive_search_url +
          "?q=" + query +
          "&resources[type]=" + resultTypes +
          "&resources[limit]=" + limit +
          "&options[prefix]=last" +
          "&options[unavailable_products]=" + showUnavailable +
          "&section_id=predictive-search";

        var fetchOpts = { credentials: "same-origin" };
        if (signal) fetchOpts.signal = signal;

        fetch(url, fetchOpts)
          .then(function (response) {
            if (!response.ok) {
              this.close();
              throw new Error(String(response.status));
            }
            return response.text();
          }.bind(this))
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, "text/html");
            var section = doc.querySelector("#shopify-section-predictive-search");
            if (!section) {
              this.close();
              return;
            }
            var resultsMarkup = section.innerHTML;

            // Sync cache across instances (no forEach dependency)
            try {
              for (var i = 0; i < this.allPredictiveSearchInstances.length; i++) {
                var inst = this.allPredictiveSearchInstances[i];
                inst.cachedResults[queryKey] = resultsMarkup;
              }
            } catch (e) {}

            this.renderSearchResults(resultsMarkup);
          }.bind(this))
          .catch(function (error) {
            if (isAbortError(error)) return;
            this.close();
            console.warn("[predictive-search] fetch error:", error);
          }.bind(this));
      } catch (e) {
        try { this.close(); } catch (_) {}
      }
    }

    setLiveRegionLoadingState() {
      try {
        this.statusElement = this.statusElement || safeQuery(this, ".predictive-search-status");
        this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

        if (this.loadingText) this.setLiveRegionText(this.loadingText);
        this.setAttribute("loading", true);
      } catch (e) {}
    }

    setLiveRegionText(statusText) {
      try {
        if (!this.statusElement) return;
        this.statusElement.setAttribute("aria-hidden", "false");
        this.statusElement.textContent = statusText;

        setTimeout(function () {
          try { this.statusElement.setAttribute("aria-hidden", "true"); } catch (e) {}
        }.bind(this), 1000);
      } catch (e) {}
    }

    renderSearchResults(resultsMarkup) {
      try {
        if (!this.predictiveSearchResults) return;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.setAttribute("results", true);

        this.setLiveRegionResults();
        this.open();
      } catch (e) {}
    }

    setLiveRegionResults() {
      try {
        this.removeAttribute("loading");
        var countEl = safeQuery(this, "[data-predictive-search-live-region-count-value]");
        if (countEl) this.setLiveRegionText(countEl.textContent);
      } catch (e) {}
    }

    getResultsMaxHeight() {
      try {
        var header =
          document.querySelector(".header-default") ||
          document.querySelector("header") ||
          document.querySelector("[data-header]");
        if (!header) {
          this.resultsMaxHeight = Math.floor(window.innerHeight * 0.7);
          return this.resultsMaxHeight;
        }
        this.resultsMaxHeight = Math.floor(
          window.innerHeight - header.getBoundingClientRect().bottom
        );
        return this.resultsMaxHeight;
      } catch (e) {
        this.resultsMaxHeight = Math.floor(window.innerHeight * 0.7);
        return this.resultsMaxHeight;
      }
    }

    open() {
      try {
        if (!this.predictiveSearchResults) return;

        var h = this.resultsMaxHeight;
        if (!h) h = this.getResultsMaxHeight();

        // FIX: always "px"
        this.predictiveSearchResults.style.maxHeight = String(h) + "px";

        this.setAttribute("open", true);
        this.input.setAttribute("aria-expanded", true);
        this.isOpen = true;
      } catch (e) {}
    }

    close(clearSearchTerm) {
      this.closeResults(!!clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm) {
      try {
        if (clearSearchTerm) {
          this.input.value = "";
          this.removeAttribute("results");
        }

        var selected = safeQuery(this, '[aria-selected="true"]');
        if (selected) selected.setAttribute("aria-selected", false);

        this.input.setAttribute("aria-activedescendant", "");
        this.removeAttribute("loading");
        this.removeAttribute("open");
        this.input.setAttribute("aria-expanded", false);
        this.resultsMaxHeight = false;

        if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute("style");
      } catch (e) {}
    }
  }

  try {
    if (!customElements.get("predictive-search")) {
      customElements.define("predictive-search", PredictiveSearch);
    }
  } catch (e) {}
})();
